/**
 * Cognito OAuth 2.0 authorization-code + PKCE flow.
 *
 * Why PKCE: the app is a SPA without a backend-for-frontend, so a client
 * secret can't be stored safely. PKCE is Cognito's recommended public-client
 * pattern — the code_verifier stays in the browser, never leaves.
 *
 * Token storage: localStorage. Survives tab close so a refresh / new tab
 * doesn't force re-auth. The refresh token is also persisted, and a
 * silent-refresh flow swaps the access token before it expires (Cognito's
 * default access-token TTL is 1 hour). XSS risk is the standard SPA
 * tradeoff — we lean on React's auto-escaping + same-origin policy.
 */

import { ENV, appOrigin } from '../env';

const STORAGE_KEYS = {
  idToken: 'starmap.idToken',
  accessToken: 'starmap.accessToken',
  refreshToken: 'starmap.refreshToken',
  expiresAt: 'starmap.expiresAt',
  codeVerifier: 'starmap.pkce_verifier',
  oauthState: 'starmap.oauth_state',
  returnTo: 'starmap.returnTo',
} as const;

export const AUTH_CHANGE_EVENT = 'starmap.auth.changed';

function emitAuthChange(): void {
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

export interface Tokens {
  idToken: string;
  accessToken: string;
  expiresAt: number; // epoch ms
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<Uint8Array> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return new Uint8Array(hash);
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return b64url(bytes);
}

function returnToKey(state: string): string {
  return `${STORAGE_KEYS.returnTo}.${state}`;
}

function safeReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  try {
    const url = new URL(value, appOrigin());
    return url.origin === appOrigin() ? `${url.pathname}${url.search}${url.hash}` : '/';
  } catch {
    return '/';
  }
}

function clearLoginState(state: string | null = localStorage.getItem(STORAGE_KEYS.oauthState)): void {
  localStorage.removeItem(STORAGE_KEYS.codeVerifier);
  localStorage.removeItem(STORAGE_KEYS.oauthState);
  localStorage.removeItem(STORAGE_KEYS.returnTo);
  if (state) localStorage.removeItem(returnToKey(state));
  sessionStorage.removeItem(STORAGE_KEYS.returnTo);
}

export async function startLogin(returnTo: string = window.location.pathname): Promise<void> {
  const verifier = randomVerifier();
  const state = randomVerifier();
  const challenge = b64url(await sha256(verifier));
  const target = safeReturnTo(returnTo);
  // localStorage so the verifier, state, and return target survive cross-tab
  // navigation — sign-up verification emails are commonly opened in a new
  // tab/browser, which would otherwise leave sessionStorage empty and break
  // the callback.
  localStorage.setItem(STORAGE_KEYS.codeVerifier, verifier);
  localStorage.setItem(STORAGE_KEYS.oauthState, state);
  localStorage.setItem(returnToKey(state), target);
  localStorage.setItem(STORAGE_KEYS.returnTo, target);

  const url = new URL(`${ENV.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set('client_id', ENV.cognitoClientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', `${appOrigin()}/auth/callback`);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string; // refresh grant doesn't always reissue this
  expires_in: number;
}

function persistTokens(json: TokenResponse): Tokens {
  const expiresAt = Date.now() + json.expires_in * 1000;
  localStorage.setItem(STORAGE_KEYS.idToken, json.id_token);
  localStorage.setItem(STORAGE_KEYS.accessToken, json.access_token);
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
  if (json.refresh_token) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, json.refresh_token);
  }
  emitAuthChange();
  return { idToken: json.id_token, accessToken: json.access_token, expiresAt };
}

export async function completeLogin(code: string, state?: string | null): Promise<string> {
  const verifier = localStorage.getItem(STORAGE_KEYS.codeVerifier);
  if (!verifier) throw new Error('Missing PKCE verifier — did you land here without starting login?');
  const expectedState = localStorage.getItem(STORAGE_KEYS.oauthState);
  if (state && expectedState && state !== expectedState) {
    clearLoginState(expectedState);
    throw new Error('OAuth state mismatch — restart sign-in.');
  }
  const stateKey = state ?? expectedState;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: ENV.cognitoClientId,
    code,
    redirect_uri: `${appOrigin()}/auth/callback`,
    code_verifier: verifier,
  });

  const res = await fetch(`${ENV.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  }
  const json = (await res.json()) as TokenResponse;
  persistTokens(json);
  const storedReturnTo =
    (stateKey ? localStorage.getItem(returnToKey(stateKey)) : null) ??
    localStorage.getItem(STORAGE_KEYS.returnTo) ??
    sessionStorage.getItem(STORAGE_KEYS.returnTo);
  clearLoginState(stateKey);

  return safeReturnTo(storedReturnTo);
}

export function getTokens(): Tokens | null {
  const idToken = localStorage.getItem(STORAGE_KEYS.idToken);
  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
  const expRaw = localStorage.getItem(STORAGE_KEYS.expiresAt);
  if (!idToken || !accessToken || !expRaw) return null;
  const expiresAt = Number(expRaw);
  if (Number.isNaN(expiresAt)) {
    clearTokens();
    return null;
  }
  // Keep returning the (possibly expired) tokens — callers wanting a still-
  // valid one should call ensureFreshTokens() instead. RequireAuth treats
  // "any tokens present" as "we have a Cognito session", and the silent
  // refresher takes over rotating before expiry.
  return { idToken, accessToken, expiresAt };
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.idToken);
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.expiresAt);
  localStorage.removeItem('starmap.activeRaterPid');
  clearLoginState();
  emitAuthChange();
}

export function logout(): void {
  clearTokens();
  const url = new URL(`${ENV.cognitoDomain}/logout`);
  url.searchParams.set('client_id', ENV.cognitoClientId);
  url.searchParams.set('logout_uri', `${appOrigin()}/auth/logout`);
  window.location.assign(url.toString());
}

/**
 * A refresh that could not reach a verdict: the network failed, Cognito
 * returned a server/throttling error, or the success body was unusable.
 * Stored tokens are left untouched so the caller can retry.
 */
export class AuthRefreshError extends Error {
  constructor(
    public readonly reason: 'network' | 'server' | 'malformed',
    public readonly status?: number,
  ) {
    super(`Sign-in refresh failed (${reason}${status ? ` ${status}` : ''})`);
    this.name = 'AuthRefreshError';
  }
}

const OAUTH_REJECTION_ERRORS = new Set(['invalid_grant', 'invalid_client', 'unauthorized_client']);

async function oauthErrorCode(res: Response): Promise<string | null> {
  try {
    const body = JSON.parse(await res.text()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : null;
  } catch {
    return null;
  }
}

function isTokenResponse(value: unknown): value is TokenResponse {
  const v = value as Partial<TokenResponse> | null;
  return !!v
    && typeof v.id_token === 'string' && v.id_token.length > 0
    && typeof v.access_token === 'string' && v.access_token.length > 0
    && typeof v.expires_in === 'number' && Number.isFinite(v.expires_in) && v.expires_in > 0
    && (v.refresh_token === undefined || typeof v.refresh_token === 'string');
}

/**
 * Silent refresh against Cognito's /oauth2/token with grant_type=refresh_token.
 * Single-flight: concurrent callers share one in-flight request so we never
 * burn through refresh attempts.
 *
 * Returns the new tokens, or null when there is no refresh token or Cognito
 * explicitly rejected it (the session is cleared). Throws AuthRefreshError
 * when the outcome is unknown; the session is preserved.
 */
let refreshPromise: Promise<Tokens | null> | null = null;

export function refreshTokens(): Promise<Tokens | null> {
  if (refreshPromise) return refreshPromise;
  const attempt: Promise<Tokens | null> = (async () => {
    // Yield first so the finally below runs after `refreshPromise` is assigned.
    await Promise.resolve();
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return null;

      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: ENV.cognitoClientId,
        refresh_token: refreshToken,
      });

      let res: Response;
      try {
        res = await fetch(`${ENV.cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
      } catch {
        throw new AuthRefreshError('network');
      }

      if (!res.ok) {
        // Refresh token rejected (revoked, expired, app-client rotated).
        // Wipe everything so RequireAuth restarts the auth-code flow.
        if (res.status === 400 || res.status === 401
            || OAUTH_REJECTION_ERRORS.has((await oauthErrorCode(res)) ?? '')) {
          clearTokens();
          return null;
        }
        throw new AuthRefreshError('server', res.status);
      }
      let json: unknown;
      try {
        json = await res.json();
      } catch {
        throw new AuthRefreshError('malformed', res.status);
      }
      if (!isTokenResponse(json)) throw new AuthRefreshError('malformed', res.status);
      return persistTokens(json);
    } finally {
      refreshPromise = null;
    }
  })();
  refreshPromise = attempt;
  return attempt;
}

/**
 * Returns the current tokens if they're not about to expire, otherwise
 * runs a silent refresh and returns the fresh ones (or null when the session
 * is gone). Throws AuthRefreshError when the refresh outcome is unknown.
 *
 * The 60s skew guards against fetch-in-flight expiry — we'd rather refresh
 * a minute early than have a request 401 mid-flight.
 */
const REFRESH_SKEW_MS = 60_000;

export async function ensureFreshTokens(): Promise<Tokens | null> {
  const cur = getTokens();
  if (!cur) return null;
  if (cur.expiresAt - Date.now() > REFRESH_SKEW_MS) return cur;
  return refreshTokens();
}

/**
 * Decode the ID token payload without signature verification — safe only for
 * pulling email/sub/groups for display. The API validates signatures server-side.
 */
export interface IdClaims {
  sub: string;
  email: string;
  'cognito:groups'?: string[];
}

export function decodeIdToken(idToken: string): IdClaims | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as IdClaims;
  } catch {
    return null;
  }
}
